import React, {
  forwardRef,
  useRef,
  FocusEventHandler,
  MouseEventHandler,
  KeyboardEventHandler,
  ReactElement,
  useCallback,
} from "react";
import classNames from "classnames";
import uniqueId from "lodash.uniqueid";
import { StepProps } from "./step.types";
import {
  showSSRHydrationWarning,
  isPropEmpty,
} from "../utils/SSR/showSSRHydrationWarning";
import { useSSR } from "../utils/SSR/useSSR";

export interface StepWithForwardRef
  extends React.ForwardRefExoticComponent<
    StepProps & React.RefAttributes<HTMLAnchorElement>
  > {}

export const Step = forwardRef<HTMLAnchorElement, StepProps>(
  (
    {
      label,
      className,
      disabled,
      href = "#",
      id,
      subLabel,
      testId,
      _privateProps = {},
      ...rest
    },
    ref
  ) => {
    const {
      active,
      completed,
      completedAriaLabel,
      condensed,
      noLine,
      currentAriaLabel,
      dropdown,
      expanded,
      isFirstActiveItem,
      isLastActiveItem,
      lastActiveItem,
      stepNumber,
      onKeyDownLastStep,
      onClick,
    } = _privateProps;

    const stepClasses = classNames(
      "spark-step-indicator__item",
      { "spark-step-indicator__item--completed": completed },
      { "spark-step-indicator__item--current": active },
      { "spark-step-indicator__item--disabled": disabled },
      { noline: noLine },
      className
    );
    useSSR(() => isPropEmpty(id) && showSSRHydrationWarning("id", "Step"));
    const { current: uniqueStepId } = useRef(uniqueId("step_"));
    const stepId = id || uniqueStepId;

    const onClickHandler: MouseEventHandler<HTMLAnchorElement> = (e) => {
      if (disabled) {
        e.preventDefault();
      } else {
        if (href === "#") {
          e.preventDefault();
        }
        onClick?.(e, stepNumber);
      }
    };

    const onFocusHandler: FocusEventHandler<HTMLAnchorElement> = (e): void => {
      active && e.target.focus();
    };

    /**
     * At the end of tabbing through the list of Steps, tabbing forward from the last active Step
     * should trigger callback to bring focus back to first menu item
     * @param {Object} e
     */
    const onListEnd: KeyboardEventHandler<HTMLAnchorElement> = (e): void => {
      // Tab forward
      if (!e.shiftKey && e.key === "Tab") {
        if (isLastActiveItem) {
          onKeyDownLastStep?.(e, 0);
        }
      }
      // Tab backward
      else if (e.shiftKey && e.key === "Tab") {
        if (isFirstActiveItem) {
          onKeyDownLastStep?.(e, lastActiveItem);
        }
      }
      // Esc from list
      else if (e.key === "Escape") {
        onKeyDownLastStep?.(e);
      }
    };

    const renderAssistiveText = useCallback(
      (): ReactElement | null =>
        active || completed ? (
          <span className="spark-assistive-text">
            {active && completed
              ? `${completedAriaLabel}, ${currentAriaLabel}`
              : active
              ? currentAriaLabel
              : completedAriaLabel}
          </span>
        ) : null,
      [active, completed, completedAriaLabel, currentAriaLabel]
    );

    /**
     * Renders content inside each list link - label and subLabel.
     * On condensed variant only, labels and subLabels are only visible as tooltips on click
     * and hover, where a character space is needed between label and subLabel because
     * they are on one line.
     */
    const ListItems = (): React.ReactElement => {
      return (
        <>
          <span className="spark-step-indicator__icon" aria-hidden={!active}>
            {stepNumber}
          </span>
          <span id={stepId} className="spark-step-indicator__text">
            {renderAssistiveText()}
            {label}
            {subLabel && (
              <small className="spark-step-indicator__subtext">
                {condensed && " "}({subLabel})
              </small>
            )}
          </span>
        </>
      );
    };

    // tabIndex is rendered on list items only when dropdown is collapsed, however,
    // this separation prevents tabIndex and onKeyDown from rendering on non-dropdown variants too.
    if (dropdown) {
      return (
        <a
          aria-labelledby={stepId}
          className={stepClasses}
          href={href}
          onClick={onClickHandler}
          onFocus={onFocusHandler}
          onKeyDown={onListEnd}
          ref={ref}
          tabIndex={!expanded || (expanded && disabled) ? -1 : undefined}
          data-testid={testId}
          {...rest}
        >
          <ListItems />
        </a>
      );
    } else {
      return (
        <a
          aria-labelledby={stepId}
          className={stepClasses}
          href={href}
          onClick={onClickHandler}
          onFocus={onFocusHandler}
          ref={ref}
          tabIndex={disabled ? -1 : undefined}
          data-testid={testId}
          {...rest}
        >
          <ListItems />
        </a>
      );
    }
  }
) as StepWithForwardRef;

Step.displayName = "Step";
